import { useTranslate } from 'react-admin';
import {
  Alert,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { QueueList } from 'src/pages/admin/services/vaultOperationsService';
import type { ApprovalRow } from 'src/pages/admin/services/vaultService';

import { OPERATIONS_NS as NS, RUNBOOK_URLS } from './constants';
import { durationText, minutesUntil, money2, sumUsdc } from './helpers';
import { AddressLink, EmptyState, ItemCard, ListNotices, TabIntro, TimeText, useNow } from './parts';
import { laneKey, waitingSinceOf } from './signers';
import type { Queue } from './useQueue';

interface Props {
  queue: Queue<QueueList<ApprovalRow>>;
  vaultName: (vaultId: string | null | undefined) => string;
  /** XBIT-5159: whether a Cold → Hot top-up waits in "Waiting for signers"; undefined when that list was not read. */
  topUpWaiting?: boolean;
}

/** XBIT-5159: Hot cannot cover it and nothing holds it, so it waits for funds alone; a held one stays a card. */
const isShortOnly = (row: ApprovalRow): boolean => !row.heldBy && row.status === 'AWAITING_FLOAT';

const SHORT_COLUMNS = ['vault', 'amount', 'destination', 'waiting'] as const;

/**
 * The two statuses a hold can stop an order at; `heldBy` is null from BROADCAST on, where a hold can no longer stop it.
 * Anything else gets this tab's own `what.unknown`, NOT the signers tab's — that one ends "Ask an engineer before
 * acting", which would contradict the "nothing to do" line on the same card.
 */
const HELD_AT: ReadonlySet<string> = new Set(['SIGNED', 'AWAITING_FLOAT']);

/**
 * XBIT-5003: treasury payouts a hold has stopped at `SIGNED` or `AWAITING_FLOAT`. A hold keeps the order's status and writes
 * no transition, so such an order moves in no queue and reached no tab: `AWAITING_QUORUM` holds show on the signers tab,
 * these showed nowhere.
 *
 * Read-only, and with no Turnkey link: there is nothing for a person to do. The order resumes by itself once every source in
 * `heldBy` is cleared, and signing would not start the money. The lane, status and destination words are the signers tab's,
 * because the vocabulary is the same one and a second copy of it would drift.
 */
export const HeldTab = ({ queue, vaultName, topUpWaiting }: Props) => {
  const translate = useTranslate();
  const now = useNow();
  const t = (key: string, options?: object) => translate(`${NS}.held.${key}`, options);
  const sig = (key: string, options?: object) => translate(`${NS}.signers.${key}`, options);
  const items = queue.data?.items ?? [];
  const short = items.filter(isShortOnly);
  const held = items.filter((row) => !isShortOnly(row));
  const shortTotal = money2(sumUsdc(short.map((row) => row.amount)));
  return (
    <Stack spacing={2}>
      <TabIntro tab="held" />
      {queue.data && (
        <ListNotices
          consistent={queue.data.consistent}
          truncated={queue.data.truncated}
          shown={items.length}
          onRefresh={queue.refetch}
        />
      )}
      {/*
        Only when the whole list was read. `truncated` counts rows READ and the filter drops unheld ones, so a cut
        read can keep nothing — and "no payout is being held" would then assert absence from a partial read.
      */}
      {queue.data && items.length === 0 && !queue.data.truncated && (
        <EmptyState tab="held" checkedAt={queue.data.checkedAt} />
      )}
      {short.length > 0 && (
        <Alert
          severity="warning"
          data-testid="short-summary"
          action={
            <Link href={RUNBOOK_URLS.held} target="_blank" rel="noopener noreferrer" variant="body2">
              {t('short.runbook')}
            </Link>
          }
        >
          {short.length === 1
            ? t('short.summaryOne', { amount: shortTotal })
            : t('short.summaryMany', { count: short.length, amount: shortTotal })}{' '}
          {topUpWaiting === false && t('short.fundCold')}
          {topUpWaiting === true && t('short.topUpWaiting')}
        </Alert>
      )}
      {short.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {SHORT_COLUMNS.map((col) => (
                  <TableCell key={col} align={col === 'amount' ? 'right' : 'left'}>
                    {t(`short.columns.${col}`)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {short.map((row) => {
                const waited = minutesUntil(waitingSinceOf(row), now);
                return (
                  <TableRow key={row.workOrderId} data-testid={`short-${row.workOrderId}`}>
                    <TableCell>{vaultName(row.vaultId)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {money2(row.amount)}
                    </TableCell>
                    <TableCell>
                      <AddressLink address={row.destination} />
                    </TableCell>
                    <TableCell>{waited === null ? '—' : durationText(waited, translate)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {held.map((row) => {
        const lane = laneKey(row.lane);
        const laneText = sig(`lane.${lane}`);
        const named = row.vaultId ? vaultName(row.vaultId) : null;
        const vault = named && named !== row.vaultId ? named : null;
        // a status with no words of its own would render as its key; only these two carry a hold
        const known = HELD_AT.has(row.status);
        return (
          <ItemCard
            key={row.workOrderId}
            testId={`held-${row.workOrderId}`}
            title={vault ? `${vault} — ${laneText}` : laneText}
            // Its own word. 'HELD' is the quarantine refund hold's ("an operator stopped the automatic
            // refund"), wrong actor and wrong object on a freeze; 'WAITING_AUTOMATIC' promises "the system
            // resolves this by itself", and a stopped payout resumes only once a PERSON clears the source.
            status="STOPPED"
            whatHappened={
              known
                ? sig(`what.${row.status}`, { lane: laneText, amount: money2(row.amount) })
                : t('what.unknown', { lane: laneText, amount: money2(row.amount) })
            }
            affected={sig(`affected.${lane}`, { vault: vault ?? sig('unknownVault') })}
            extra={
              <Stack spacing={0.5}>
                <Typography variant="body2" component="div">
                  {sig('destination')} <AddressLink address={row.destination} />
                </Typography>
                {/*
                  The whole point of the tab. `heldBy` is the engine's joined list of EVERY source holding this order, so it
                  is shown verbatim: splitting it, trimming it or special-casing "(unreadable)" would let an operator clear
                  one cause, find the money still stopped, and hunt a cause that is on screen nowhere.
                */}
                <Typography variant="body2" sx={{ color: 'warning.main', overflowWrap: 'anywhere' }}>
                  {row.heldBy ? t('heldBy', { sources: row.heldBy }) : t('heldByUnreported')}
                </Typography>
                {/*
                  A short float is a second thing stopping the money, and it is NOT one of the heldBy sources.
                  Without this line the card says the account is short, names the hold, then promises it moves
                  "once every source above is cleared" — so an operator clears the freeze, nothing moves, and the
                  remaining cause reads as a status rather than as a condition. That is this ticket's own failure.
                */}
                {row.status === 'AWAITING_FLOAT' && (
                  <Typography variant="body2" sx={{ color: 'warning.main' }}>
                    {t('alsoShortOfFloat')}
                  </Typography>
                )}
              </Stack>
            }
            deadline={
              <>
                {t('noDeadline')} {t('createdAt')} <TimeText iso={row.createdAt} />
              </>
            }
            actions={<Typography variant="body2">{t('nothingToDo')}</Typography>}
            technical={{
              workOrderId: row.workOrderId,
              lane: row.lane,
              status: row.status,
              heldBy: row.heldBy ?? null,
              stuckReason: row.stuckReason,
              vaultId: row.vaultId,
              redemptionRecordId: row.redemptionRecordId,
              destination: row.destination,
              activityId: row.activityId,
              activityLive: row.activityLive,
              createdAt: row.createdAt,
            }}
          />
        );
      })}
    </Stack>
  );
};
